/**
 * Speech-to-text via the Web Speech API.
 *
 * Why not a native Capacitor plugin? `webkitSpeechRecognition` works inside
 * iOS WKWebView (15.0+) by deferring to Apple's online dictation, and on
 * Chromium-based browsers via Google's service. Permission is gated by
 * `NSMicrophoneUsageDescription` in Info.plist — no native bridge needed.
 *
 * Single live transcription session — caller's `onText` receives interim
 * + final transcript as the user speaks; `stop()` finalizes and ends the
 * mic capture. We deliberately do NOT auto-send; the textarea owns the
 * value and the user reviews/edits before tapping Send.
 */

type SpeechRecognitionLike = {
  continuous: boolean
  interimResults: boolean
  lang: string
  start(): void
  stop(): void
  abort(): void
  onresult: ((e: any) => void) | null
  onerror: ((e: any) => void) | null
  onend: (() => void) | null
}

function getCtor(): (new () => SpeechRecognitionLike) | null {
  const w = window as any
  return w.SpeechRecognition || w.webkitSpeechRecognition || null
}

export function isSttAvailable(): boolean {
  return !!getCtor()
}

export interface SttSession {
  stop(): void
}

export interface SttCallbacks {
  /** Called with the running transcript (interim + final concatenated). */
  onText: (text: string) => void
  /** Called once the session ends, regardless of cause. */
  onEnd?: () => void
  /** Called on a recoverable / non-recoverable error. */
  onError?: (msg: string) => void
}

export function startStt({ onText, onEnd, onError }: SttCallbacks): SttSession | null {
  const Ctor = getCtor()
  if (!Ctor) return null
  let rec: SpeechRecognitionLike
  try { rec = new Ctor() } catch { return null }
  rec.continuous = true
  rec.interimResults = true
  rec.lang = navigator.language || 'en-US'

  let finalText = ''

  rec.onresult = (e: any) => {
    let interim = ''
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const chunk = e.results[i][0]?.transcript || ''
      if (e.results[i].isFinal) finalText += (finalText ? ' ' : '') + chunk.trim()
      else interim += chunk
    }
    const combined = (finalText + (interim ? ' ' + interim : '')).trim()
    onText(combined)
  }
  rec.onerror = (e: any) => {
    const code = e?.error || 'unknown'
    // 'no-speech' fires when the user is silent for too long — common,
    // not worth surfacing. Everything else is.
    if (code !== 'no-speech') onError?.(code)
  }
  rec.onend = () => { onEnd?.() }

  try { rec.start() } catch (e: any) {
    onError?.(e?.message || 'failed to start')
    return null
  }
  return { stop: () => { try { rec.stop() } catch { /* ignore */ } } }
}
